import { FormControl } from '@mui/material';
import { DateField, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import { Controller, useFormContext } from 'react-hook-form';

import { FormElements } from '@/pages/components/form-fields/FormType';
import MedicareNumber from '@/pages/components/form-fields/input/MedicareNumber';

const MEDICARE_NUMBER_LENGTH = 10;

export const MedicareNumberField = () => {
  const { control, trigger } = useFormContext();

  return (
    <FormControl sx={{ my: 2 }} fullWidth>
      <Controller
        control={control}
        name="medicare.number"
        defaultValue=""
        rules={{
          required: true,
          validate: (value) => String(value ?? '').length === MEDICARE_NUMBER_LENGTH || 'Please enter all 10 digits',
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <MedicareNumber
            name="medicare.number"
            label="1. Medicare number"
            type={FormElements.medicareNumber}
            value={(value as string) ?? ''}
            onChange={(next) => {
              onChange(next);
              void trigger('medicare.number');
            }}
            error={!!error}
            helperText={error?.message ?? 'The 10 digit number at the top of your card'}
            variant="filled"
            inputProps={{ format: '#### ##### #' }}
          />
        )}
      />
    </FormControl>
  );
};

export const MedicareExpiryField = () => {
  const { control, trigger } = useFormContext();

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <FormControl sx={{ my: 1 }} fullWidth>
        <Controller
          control={control}
          name="medicare.expiryDate"
          defaultValue=""
          rules={{
            required: true,
            validate: (value) => {
              const date = value ? DateTime.fromISO(value) : null;
              if (!date?.isValid) {
                return 'Please enter a valid date';
              }
              // A card stays valid through the end of the month it names, so compare
              // against the start of this month rather than today.
              if (date < DateTime.now().startOf('month')) {
                return 'This card has expired';
              }
              return true;
            },
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DateField
              label="4. Expiry date"
              format="MM/yyyy"
              value={value ? DateTime.fromISO(value) : null}
              onChange={(date) => {
                // A half-typed date is not valid yet; store null rather than an
                // unparseable string so the rule reports it as such.
                onChange(date?.isValid ? date.toISO() : null);
                void trigger('medicare.expiryDate');
              }}
              disablePast
              slotProps={{
                textField: {
                  variant: 'filled',
                  error: !!error,
                  helperText: error?.message ?? 'The date in MM/YYYY format on the bottom right of your card',
                },
              }}
            />
          )}
        />
      </FormControl>
    </LocalizationProvider>
  );
};
